import { AppShell, AppContent, AppShellMenuButton } from "@sylviq/ui-react-web/components/shell/AppShell";
import { SidebarAccountMenu } from "@sylviq/ui-react-web/components/shell/SidebarAccountMenu";
import { Header } from "@sylviq/ui-react-web/components/ui/header";
import { MonochromeMark } from "@sylviq/ui-react-web/components/ui/MonochromeMark";
import { ProductLockup } from "@sylviq/ui-react-web/components/shell/ProductLockup";
import {
  AppNavigation,
  type AppNavigationDestination,
  type AppNavigationIcon,
  type AppNavigationSection,
} from "@sylviq/ui-react-web/blocks/navigation";
import { Blocks, BookOpen, Building2, Database, KeyRound, LayoutGrid, Settings } from "lucide-react";
import { type MouseEvent, type ReactNode, useEffect, useState } from "react";
import { listExtensionInstallations } from "@/lib/api";
import { useSession } from "@/lib/session";
import {
  organizationSettingsTabs,
  type OrganizationSettingsTab,
} from "@/features/organizations/organizationSettings";
import {
  OrganizationSwitcher,
  type ConsoleOrganizationOption,
} from "@/features/organizations/OrganizationSwitcher";
import { EXTENSION_INSTALLATIONS_CHANGED_EVENT } from "@/features/extensions/extension-events";

export type { ConsoleOrganizationOption } from "@/features/organizations/OrganizationSwitcher";

export type OrganizationView = "entities" | "data" | "secrets" | "extensions" | "access";
export const organizationViews = {
  entities: { title: "Legal entities", description: "Manage the legal entities that keep books in this organization.", icon: Building2 },
  data: { title: "Data", description: "Export or restore a legal entity's books as a snapshot.", icon: Database },
  secrets: { title: "Secrets", description: "Manage write-only organization secrets and see which extensions can use them.", icon: KeyRound },
  extensions: { title: "Extensions", description: "Browse, manage installed extensions, and curate your own scripts.", icon: Blocks },
  access: { title: "Developer access", description: "Manage API tokens and external MCP connections.", icon: KeyRound },
} as const;

export function organizationViewHref(organizationId: string, view: OrganizationView): string {
  return `/organizations/${encodeURIComponent(organizationId)}/${view}`;
}

interface OrganizationNavigationDestination {
  href: string;
}

const appsDestination = { id: "apps", label: "Apps", icon: LayoutGrid, href: "/home" } as const;
const documentationDestination = {
  id: "documentation",
  label: "Documentation",
  icon: BookOpen,
  href: "/docs",
} as const;

function destinationSection(
  destination: OrganizationNavigationDestination & {
    id: string;
    label: string;
    icon: AppNavigationIcon;
  },
  active: boolean,
  hidden = false,
): AppNavigationSection<OrganizationNavigationDestination> {
  return {
    id: destination.id,
    label: destination.label,
    icon: destination.icon,
    active,
    hidden,
    destination: { href: destination.href },
  };
}

/** App-owned destinations; all navigation layout and behavior are shared UI. */
export function OrganizationNavigation({ organizationId, organizations, onOrganizationChange, view, activeSettingsId, children, onNavigate, onSignOut }: {
  organizationId?: string;
  organizations?: readonly ConsoleOrganizationOption[];
  onOrganizationChange?: (organizationId: string) => void;
  view: OrganizationView | "apps" | "settings";
  activeSettingsId?: string;
  children: ReactNode;
  onNavigate?: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
  onSignOut?: () => void;
}) {
  const { state, signOut } = useSession();
  const organizationChoices = organizations ?? [];
  const selectedOrganization = organizationChoices.find((organization) => organization.id === organizationId);
  const showSecrets =
    selectedOrganization?.canManageSecrets === true &&
    state.status === "authenticated" &&
    state.user.mfa_verified !== false;
  const [extensionTabs, setExtensionTabs] = useState<OrganizationSettingsTab[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!organizationId || !selectedOrganization) {
      setExtensionTabs([]);
      return;
    }
    const loadTabs = () => {
      void (async () => {
        try {
          const installations = await listExtensionInstallations(organizationId);
          if (!cancelled) setExtensionTabs(organizationSettingsTabs(installations, organizationId));
        } catch {
          if (!cancelled) setExtensionTabs([]);
        }
      })();
    };
    loadTabs();
    window.addEventListener(EXTENSION_INSTALLATIONS_CHANGED_EVENT, loadTabs);
    return () => {
      cancelled = true;
      window.removeEventListener(EXTENSION_INSTALLATIONS_CHANGED_EVENT, loadTabs);
    };
  }, [organizationId, selectedOrganization]);
  const sections: AppNavigationSection<OrganizationNavigationDestination>[] = [
    destinationSection(appsDestination, view === "apps"),
    ...(organizationId
      ? (Object.entries(organizationViews) as [OrganizationView, typeof organizationViews[OrganizationView]][]).map(
          ([key, item]) =>
            destinationSection(
              { id: key, label: item.title, icon: item.icon, href: organizationViewHref(organizationId, key) },
              view === key,
              key === "secrets" && !showSecrets,
            ),
        )
      : []),
    ...extensionTabs.map((tab) =>
      destinationSection(
        { id: `settings-${tab.id}`, label: tab.label, icon: Settings, href: tab.href },
        view === "settings" && activeSettingsId === tab.id,
      ),
    ),
    destinationSection(documentationDestination, false),
  ];
  const renderDestination = (destination: AppNavigationDestination<OrganizationNavigationDestination>) => {
    const href = destination.meta!.href;
    return <a href={href} onClick={(event) => onNavigate?.(event, href)} />;
  };
  return (
    <AppShell>
      <Header
        left={<AppShellMenuButton />}
        right={
          organizations !== undefined && onOrganizationChange ? (
            <OrganizationSwitcher
              organizationId={organizationId}
              organizations={organizations}
              onChange={onOrganizationChange}
              onNavigate={onNavigate}
            />
          ) : undefined
        }
      />
      <AppNavigation
        className="app-sidebar--compact"
        label="Organization navigation"
        sections={sections}
        renderDestination={renderDestination}
        lockup={<ProductLockup asChild mark={<MonochromeMark src="/brand/sylviq-mark-3.svg" className="h-10 w-10" label={null} />} wordmark="SYLVIQ"><a href="/home" aria-label="SYLVIQ Console home" onClick={(event) => onNavigate?.(event, "/home")} /></ProductLockup>}
        footer={state.status === "authenticated" ? <SidebarAccountMenu user={{email: state.user.email, displayName: state.user.username}} accountHref="/home" accountLabel="Your apps" onSignOut={() => onSignOut ? onSignOut() : void signOut()} /> : undefined}
      />
      <AppContent>{children}</AppContent>
    </AppShell>
  );
}
